/**
 * Upload drill-in (DATA_VISUALIZATION_FRONTEND_PLAN §2.3).
 *
 * Server-paginated table over `GET /uploads/{id}/rows`. Columns are derived
 * dynamically from the keys of `rows[].data` (the jsonb shape differs per
 * upload type). When the rows carry a date-like column plus ≥ 1 numeric
 * column, a "Chart" view plots them in the same SeriesChart.
 */
import { isApiError } from "@compliance/domain";
import {
  Alert,
  Drawer,
  Group,
  Loader,
  Pagination,
  ScrollArea,
  SegmentedControl,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconAlertCircle, IconChartLine, IconTable } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useUploadRows } from "../../api/hooks";
import type { UploadRowEntry } from "../../api/types";
import { deriveColumns, looksDate, looksNumeric } from "../../lib/uploadColumns";
import { SeriesChart } from "./SeriesChart";
import { colorForIndex } from "./colors";
import type { ChartSeriesInput } from "./series-data";

const PAGE_SIZE = 200;

type Props = {
  uploadId: string | null;
  title?: string;
  opened: boolean;
  onClose: () => void;
};

export function UploadRowsDrawer({ uploadId, title, opened, onClose }: Props) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Stack gap={0}>
          <Text fw={600}>{title ?? "Parsed rows"}</Text>
          {uploadId ? (
            <Text size="xs" c="dimmed" ff="monospace">
              {uploadId}
            </Text>
          ) : null}
        </Stack>
      }
    >
      {/* Keyed on uploadId so switching uploads remounts the body and resets
          its paging/view (held inside RowsBody) back to the first page. */}
      {uploadId ? <RowsBody key={uploadId} uploadId={uploadId} /> : null}
    </Drawer>
  );
}

function RowsBody({ uploadId }: { uploadId: string }) {
  // Paging/view live here (not in the parent) so the parent's `key={uploadId}`
  // remount resets them when the drawer targets a different upload.
  const [offset, setOffset] = useState(0);
  const [view, setView] = useState<"table" | "chart">("table");
  const rowsQ = useUploadRows(uploadId, { limit: PAGE_SIZE, offset });
  const rows = rowsQ.data?.rows ?? [];
  const total = rowsQ.data?.total_rows ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const page = Math.floor(offset / PAGE_SIZE) + 1;

  const columns = useMemo(() => deriveColumns(rows), [rows]);
  const chartable = useMemo(() => detectChartable(rows, columns), [rows, columns]);

  if (rowsQ.isPending) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  if (rowsQ.isError) {
    return (
      <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
        <Text size="sm">
          {isApiError(rowsQ.error) ? rowsQ.error.message : "Failed to load rows."}
        </Text>
      </Alert>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" wrap="wrap">
        <Text size="sm" c="dimmed">
          {total.toLocaleString()} row{total === 1 ? "" : "s"} · showing{" "}
          {rows.length ? `${offset + 1}–${offset + rows.length}` : "0"}
        </Text>
        <SegmentedControl
          size="xs"
          value={view}
          onChange={(v) => setView(v as "table" | "chart")}
          data={[
            { value: "table", label: <SegLabel icon={<IconTable size={14} />} text="Table" /> },
            {
              value: "chart",
              label: <SegLabel icon={<IconChartLine size={14} />} text="Chart" />,
              disabled: !chartable,
            },
          ]}
        />
      </Group>

      {view === "chart" && chartable ? (
        <Stack gap={4}>
          <SeriesChart series={chartable.series} height={300} />
          <Text size="xs" c="dimmed">
            Charting the current page ({rows.length} rows) by{" "}
            <Text span ff="monospace">
              {chartable.dateColumn}
            </Text>
            .
          </Text>
        </Stack>
      ) : (
        <ScrollArea.Autosize mah={520} type="auto">
          <Table fz="xs" stickyHeader highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                {columns.map((c) => (
                  <Table.Th key={c}>{c}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.row_number}>
                  <Table.Td ff="monospace" c="dimmed">
                    {r.row_number}
                  </Table.Td>
                  {columns.map((c) => (
                    <Table.Td key={c} ff="monospace">
                      {renderCell(r.data[c])}
                    </Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>
      )}

      {pageCount > 1 ? (
        <Group justify="center">
          <Pagination
            size="sm"
            total={pageCount}
            value={page}
            onChange={(p) => setOffset((p - 1) * PAGE_SIZE)}
          />
        </Group>
      ) : null}
    </Stack>
  );
}

function SegLabel({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <Group gap={6} wrap="nowrap">
      {icon}
      <span>{text}</span>
    </Group>
  );
}

// ── Chartability derivation (column helpers shared via lib/uploadColumns) ────

type Chartable = { dateColumn: string; series: ChartSeriesInput[] };

/**
 * Chart-when-chartable heuristic: a date-like column + ≥ 1 numeric column.
 * Each numeric column becomes one series keyed on the date column.
 */
function detectChartable(rows: UploadRowEntry[], columns: string[]): Chartable | null {
  if (rows.length === 0) return null;
  const sample = rows[0]?.data ?? {};

  const dateColumn = columns.find((c) => looksDate(c, sample[c]));
  if (!dateColumn) return null;

  const numericColumns = columns.filter((c) => c !== dateColumn && looksNumeric(sample[c]));
  if (numericColumns.length === 0) return null;

  // Each numeric column becomes one series keyed on the date, so multiple rows
  // sharing a date (per-dairy kWh, per-meter PG&E rows) would silently collapse
  // to an arbitrary row in the chart. We can't sum them client-side (the UI must
  // never recompute a figure — see locked decision #6), so rather than show a
  // misleading chart we don't offer one here; the table still lists every row.
  const days = rows.map((r) => String(r.data[dateColumn] ?? "").slice(0, 10));
  if (new Set(days).size !== days.length) return null;

  const series: ChartSeriesInput[] = numericColumns.map((col, i) => ({
    id: col,
    label: col,
    unit: col,
    color: colorForIndex(i),
    points: rows.map((r) => ({
      day: String(r.data[dateColumn] ?? "").slice(0, 10),
      value: r.data[col] == null ? null : String(r.data[col]),
    })),
  }));

  return { dateColumn, series };
}

function renderCell(value: unknown): string {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}
